import React from 'react'

import { Box } from '../box'
import { Pressable } from '../pressable'
import { Text } from '../text'
import { chipProcessContainer } from './variants'

export type ChipProcessLayout = 'horizontal' | 'horizontal-2-lines' | 'vertical'

export type ChipProcessProps = {
    label: string
    /** Leading (horizontal) / top (vertical) icon node. */
    icon?: React.ReactNode
    layout?: ChipProcessLayout
    /** Selected chips gain the action-blue border. */
    isSelected?: boolean
    isDisabled?: boolean
    onPress?: () => void
    className?: string
    testID?: string
}

/**
 * Process chip matching the Figma redesign (AOS-603): an icon + label pill laid
 * out `horizontal` (icon leading), `horizontal-2-lines` (the 48px
 * process-selection-grid cell, whose label may run onto a second line) or
 * `vertical` (icon stacked above the label).
 * Selected chips gain the action-blue focus border; hover lightens; disabled
 * dims to 40%. Presentational and controlled. Without `onPress` the chip
 * renders as a plain read-only Box (no button role, no pointer cursor).
 */
export const ChipProcess: React.FC<ChipProcessProps> = ({
    label,
    icon,
    layout = 'horizontal',
    isSelected,
    isDisabled,
    onPress,
    className,
    testID,
}) => {
    const container = chipProcessContainer({
        layout,
        isSelected: Boolean(isSelected),
        isDisabled: Boolean(isDisabled),
        isInteractive: Boolean(onPress) && !isDisabled,
        class: className,
    })

    const isTwoLines = layout === 'horizontal-2-lines'
    const content = (
        <>
            {icon ? <Box className='shrink-0 items-center justify-center'>{icon}</Box> : null}
            <Text
                weight='semiBold'
                numberOfLines={isTwoLines ? 2 : undefined}
                className={isTwoLines ? 'min-w-0 flex-1' : undefined}
            >
                {label}
            </Text>
        </>
    )

    if (!onPress) {
        return (
            <Box className={container} accessibilityLabel={label} testID={testID}>
                {content}
            </Box>
        )
    }

    return (
        <Pressable
            className={container}
            disabled={Boolean(isDisabled)}
            onPress={isDisabled ? undefined : onPress}
            accessibilityRole='button'
            accessibilityState={{ selected: Boolean(isSelected), disabled: Boolean(isDisabled) }}
            accessibilityLabel={label}
            testID={testID}
        >
            {content}
        </Pressable>
    )
}

ChipProcess.displayName = 'ChipProcess'
